import React, { cloneElement, forwardRef, isValidElement, ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Pagination from '../pagination/Pagination';
import Checkbox from '../checkbox/Checkbox';
import { DataGridContext, useDataGrid, useDataGridContext } from './hooks';
import { DataGridColumn } from './types';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/20/solid';
import Button from '../button/Button';
import Spinner from '../spinner/Spinner';
import Dots from '../../icons/Dots';
import { TooltipV2 } from '../tooltip/TooltipV2';
import { DndContext, DragOverEvent } from '@dnd-kit/core';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

export type DataGridRef = {
    clear: () => void;
    resetPagination: () => void;
};

export type DataGridProps = {
    stickyHeader?: boolean;
    /**
     * Each row is a child component of the data grid. Each child must be an instance of DataGrid.Row.
     */
    children: ReactNode | ReactNode[];

    /**
     * The columns the grid will seperate content into.
     */
    columns: DataGridColumn[];

    /**
     * Renders the grid in a "loading" state. Grid controls will be disabled.
     */
    isLoading?: boolean;

    /**
     * Total number of rows the grig could potentially render. Used for pagination purposes.
     */
    totalRows: number;

    /**
     * Allows the user to select how many rows to see at a time. e.g. [10, 25, 50].
     */
    rowSizes: number[];

    /**
     * If data grid row should have a bottom border
     */
    showRowBorder?: boolean;

    /**
     * What row size option to default to.
     */
    defaultRowSizeIndex?: number;

    /**
     * Clears all selected rows.
     */
    clear?: boolean;

    /**
     * Hides pagination controls on the grid.
     */
    disablePagination?: boolean;

    /**
     * Message do display when there are no rows.
     */
    emptyMessage?: string | ReactNode;

    /**
     * Renders the table in an errored state.
     */
    isError?: boolean;

    /**
     * Renders a checkbox to select rows.
     */
    isSelectable?: boolean;

    /**
     * Allows rows order to be swapped using drag and drop
     */
    isDraggable?: boolean;

    /**
     * Disables drag and drop
     */
    disableDrag?: boolean;

    /**
     * Hint to show if row can't be drag
     */
    disableDragHint?: string;

    /**
     * Direction the limit change should go
     */
    limitDirection?: 'top' | 'bottom';

    /**
     * Callback function invoked when the "retry" error button is clicked.
     */
    onErrorRetry?: () => void;

    /**
     * Callback function invoked when the page is changed. Gives back the current page.
     */
    onPageChange?: (page: number) => void;

    /**
     * Callback function invoked when the row size is changed. Gives back the current row size.
     */
    onRowSizeChange?: (size: number) => void;

    /**
     * Callback function invoked when a row is selected/unselected. Gives back all selected row ids.
     */
    onSelect?: (ids: string[]) => void;

    /**
     * Callback function invoked when row is moved. Gives back new sorted row ids.
     */
    onMove?: (ids: string[]) => void;

    /**
     * Callback function invoked when drag event occurs. Gives back true if drag event is occuring otherwise false.
     */
    onDragging?: (isDragging: boolean) => void;

    /**
     * Callback function invoked when dragged row is dropped.
     */
    onDrop?: () => void;

    /**
     * Should the table be fixed in width and overflow
     */
    fixed?: boolean;
};

const DataGridForwardRef = forwardRef(function DataGrid(
    {
        stickyHeader = false,
        children,
        columns,
        isLoading,
        totalRows,
        rowSizes,
        defaultRowSizeIndex,
        onPageChange,
        onRowSizeChange,
        onSelect,
        showRowBorder = true,
        disablePagination = false,
        clear = false,
        emptyMessage = 'No data',
        isError = false,
        isSelectable = true,
        isDraggable = false,
        onErrorRetry,
        disableDrag = false,
        fixed = false,
        disableDragHint,
        onMove,
        onDragging,
        onDrop,
        limitDirection,
    }: DataGridProps,
    ref: React.ForwardedRef<DataGridRef>
): JSX.Element {
    const dataGridInstance = useDataGrid(columns, isSelectable, isDraggable, disableDrag, showRowBorder, disableDragHint, onSelect);
    const [page, setPage] = useState(1);
    const [rowLimit, setRowLimit] = useState(rowSizes[defaultRowSizeIndex || 0]);
    const [rows, setRows] = useState<string[]>(
        (
            React.Children.map(children, (child, idx) => {
                if (!child) {
                    return;
                }
                // @ts-ignore
                return child.props.id || child.key;
            }) || []
        ).filter((r) => r)
    );

    useEffect(() => {
        setRows(
            (
                React.Children.map(children, (child, idx) => {
                    if (!child) {
                        return;
                    }
                    // @ts-ignore
                    return child.props.id || child.key;
                }) || []
            ).filter((r) => r)
        );
    }, [children]);

    const gridRef = useRef<HTMLDivElement>();

    // build the css grid style string
    const gridTemplateColumns = [
        isDraggable ? '25px' : '',
        isSelectable ? '50px' : '',
        ...columns.map((column) => column.width || '1fr'),
    ].join(' ');

    function handlePageChange(page: number): void {
        setPage(page);
        onPageChange(page);
    }

    function handleRowLimitChange(limit: number): void {
        setRowLimit(limit);
        onRowSizeChange(limit);
        handlePageChange(1);
    }

    function handleDragOver(event: DragOverEvent): void {
        onDragging && onDragging(true);
        const { active, over } = event;

        // Handle when row is being dragged further than the last row (outside of container)
        if (over === null) {
            return;
        }

        // @ts-ignore
        const disabledRows = React.Children.toArray(children).filter((c) => c.props?.preventDrag === true);
        const disabledRowsIndex = disabledRows.map((row) => {
            // @ts-ignore
            return rows.indexOf(row.props?.id || row.key);
        });

        const activeRowId = rows.indexOf(active.id.toString());
        const overRowId = rows.indexOf(over.id.toString());

        for (const index of disabledRowsIndex) {
            if ((activeRowId < index && overRowId > index) || (activeRowId > index && overRowId < index)) {
                return;
            }
        }

        if (over && active.id !== over.id) {
            const oldIndex = rows.indexOf(active.id.toString());
            const newIndex = rows.indexOf(over.id.toString());
            onMove && onMove(move([...rows], oldIndex, newIndex));
        }
    }

    // eslint-disable-next-line camelcase
    function move(array: string[], oldIndex: number, newIndex: number): string[] {
        if (newIndex >= array.length) {
            newIndex = array.length - 1;
        }
        array.splice(newIndex, 0, array.splice(oldIndex, 1)[0]);
        return array;
    }

    useImperativeHandle(ref, () => ({
        clear: (): void => {
            dataGridInstance.clearAllSelected();
        },
        resetPagination: (): void => {
            setPage(1);
        },
    }));

    return (
        <DataGridContext.Provider value={dataGridInstance}>
            {/* <div className="w-full overflow-auto"> */}
            <div className={`${fixed ? ' w-max inline-block' : ''}`} id="datagrid" ref={gridRef}>
                {columns.every((c) => !c.header) && !isSelectable ? (
                    showRowBorder && <div className="border-b dark:border-white/[0.12]"></div>
                ) : (
                    <div
                        className={`border-b dark:border-white/[0.12] grid gap-4 py-2  ${stickyHeader ? 'bg-white sticky top-0 z-20' : ''}`}
                        style={{ gridTemplateColumns }}
                    >
                        {/* Select All */}
                        {isDraggable && <div></div>}
                        {isSelectable && (
                            <div className={`flex justify-center items-start bg-white ${fixed ? 'left-0 sticky' : ''}`}>
                                <Checkbox
                                    checked={dataGridInstance.allChecked}
                                    indeterminate={dataGridInstance.someChecked}
                                    onChange={(e): void => {
                                        dataGridInstance.toggleAllRowsChecked();
                                    }}
                                    disabled={React.Children.count(children) === 0}
                                />
                            </div>
                        )}

                        {columns.map((column, idx) => {
                            return (
                                <div
                                    key={idx}
                                    className="flex items-start whitespace-nowrap pl-4"
                                    style={{ justifyContent: column.alignment || 'left' }}
                                >
                                    {typeof column.header === 'string' ? (
                                        <p className="font-medium text-xs text-gray-600 dark:text-gray-400">
                                            {column.header.toUpperCase()}
                                        </p>
                                    ) : (
                                        column.header
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                {React.Children.count(children) === 0 &&
                    !isError &&
                    (typeof emptyMessage === 'string' ? (
                        <p className="mt-8 text-center text-sm text-gray-500">{emptyMessage}</p>
                    ) : (
                        <div className="mt-8 mx-auto w-fit">{emptyMessage}</div>
                    ))}

                {/* Grid Error */}
                {isError && (
                    <div className="mt-8 mx-auto w-fit flex flex-col items-center gap-2">
                        <ExclamationTriangleIcon className="text-red-600 h-7 w-7" />
                        <p className="text-sm text-gray-500">Error loading data</p>
                        <Button
                            type="secondary"
                            label="Retry"
                            leftIcon={<ArrowPathIcon />}
                            size="sm"
                            disabled={isLoading}
                            onClick={(): void => {
                                onErrorRetry && onErrorRetry();
                            }}
                        />
                    </div>
                )}

                {/* Grid Rows */}
                {!isError && (
                    <DndContext
                        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                        onDragOver={handleDragOver}
                        onDragEnd={(): void => {
                            onDrop && onDrop();
                            onDragging && onDragging(false);
                        }}
                    >
                        {/* @ts-ignore */}
                        <SortableContext items={rows}>
                            <div className={` ${isLoading ? ' opacity-30' : ''}`}>
                                {React.Children.map(children, (child, idx) => {
                                    if (isValidElement(child)) {
                                        return cloneElement(child, {
                                            index: child.props.index !== undefined ? child.props.index : idx,
                                            ...child.props,
                                        });
                                    } else {
                                        return <div></div>;
                                    }
                                })}
                            </div>
                        </SortableContext>
                    </DndContext>
                )}

                {isLoading && (
                    <div className="absolute w-full h-[200px] top-0">
                        <div className="absolute left-1/2 bottom-1/2 translate-y-[40px] opacity-100">
                            <Spinner />
                        </div>
                    </div>
                )}
            </div>
            {/* </div> */}
            {!disablePagination && !isError && React.Children.count(children) > 0 && (
                <div className="mt-4 px-8">
                    <Pagination
                        total={totalRows}
                        rowLimits={rowSizes}
                        rowLimit={rowLimit}
                        page={page}
                        defaultRowSizeIndex={defaultRowSizeIndex}
                        onPageChange={handlePageChange}
                        onRowLimitChange={handleRowLimitChange}
                        limitDirection={limitDirection}
                    />
                </div>
            )}
        </DataGridContext.Provider>
    );
});

type RowProps = {
    /**
     * Unique id used internally for row state.
     */
    id: string;

    /**
     * Allows/Disallows the row to be selected.
     */
    disabled?: boolean;

    /**
     * Automatically selectes this row.
     */
    selected?: boolean;

    /**
     * Content to render in the row. Number of children should match number of DataGrid columns for a consitent look.
     */
    children: ReactNode | ReactNode[];

    /**
     * Optional Ref for further row controls.
     */
    innerRef?: React.RefObject<HTMLDivElement>;

    /**
     * Allow user to pass in custom drag handle icon
     */
    dragHandleIcon?: ReactNode;

    /**
     * Is the row draggable
     */
    preventDrag?: boolean;

    onClick?: (id: string) => void;

    isActive?: boolean;
};

export function Row({
    children,
    id,
    dragHandleIcon,
    disabled = false,
    selected = false,
    preventDrag = false,
    innerRef,
    onClick,
    isActive = false,
}: RowProps): JSX.Element {
    innerRef = useRef<HTMLDivElement>(null);
    const dataGrid = useDataGridContext();
    const draggable = preventDrag ? false : dataGrid.isDraggable;
    // build the css grid style string
    const gridTemplateColumns = [
        dataGrid.isDraggable ? '25px' : '',
        dataGrid.isSelectable ? '50px' : '',
        ...dataGrid.columns.map((column) => column.width || '1fr'),
    ].join(' ');

    const { setNodeRef, transform, transition, attributes, listeners } = useSortable({
        id,

        disabled: { draggable: !draggable, droppable: !draggable },
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    useEffect(() => {
        dataGrid.addRow(id, disabled);
        return (): void => dataGrid.removeRow(id);
    }, [disabled, id]);

    useEffect(() => {
        dataGrid.updateRowChecked(id, Boolean(selected));
    }, [id, selected]);

    useEffect(() => {
        dataGrid.updateRowDisabled(id, disabled);
    }, [disabled, id]);

    return (
        <div
            className={`group py-1 grid gap-4 ${dataGrid.showRowBorder ? 'border-b' : ''} ${
                isActive ? 'bg-indigo-50' : dataGrid.getRowChecked(id) ? 'bg-gray-50' : 'bg-white'
            } ${onClick ? 'cursor-pointer hover:bg-indigo-50' : ''}`}
            style={{ gridTemplateColumns, ...style }}
            ref={setNodeRef}
            onClick={(e): void => {
                if (!onClick) {
                    return;
                }

                e.stopPropagation();

                if (e.target instanceof Element) {
                    if (e.target.tagName.toLowerCase() === 'div') {
                        onClick(id);
                    }
                }
            }}
        >
            {dataGrid.isDraggable && (
                <TooltipV2 position="left" message={dataGrid.disableDragHint} disabled={!dataGrid.disableDrag || !dataGrid.disableDragHint}>
                    <div className="flex justify-center items-center" ref={innerRef} data-data-grid-col>
                        {draggable && (
                            <div
                                className={`h-5 w-5 ${draggable && !dataGrid.disableDrag ? 'cursor-grab' : 'pointer-events-none'}`}
                                {...attributes}
                                {...listeners}
                            >
                                {dragHandleIcon || <Dots />}
                            </div>
                        )}
                    </div>
                </TooltipV2>
            )}
            {dataGrid.isSelectable && (
                <div className={`flex justify-center items-center left-0 sticky z-10 bg-inherit`} data-data-grid-col>
                    <Checkbox
                        checked={dataGrid.getRowChecked(id)}
                        onChange={(e): void => {
                            if (e.target.checked) {
                                dataGrid.changeLastChecked(id);
                            } else {
                                dataGrid.changeLastChecked(undefined);
                            }
                            if (e.nativeEvent instanceof PointerEvent) {
                                const shiftKeyPressed = e.nativeEvent.shiftKey;
                                if (shiftKeyPressed && dataGrid.lastChecked) {
                                    const currentIndex = dataGrid.rows.indexOf(id);
                                    const lastCheckedIndex = dataGrid.rows.indexOf(dataGrid.lastChecked);

                                    const start = Math.min(currentIndex, lastCheckedIndex);
                                    const end = Math.max(currentIndex, lastCheckedIndex);

                                    for (let i = start; i <= end; i++) {
                                        dataGrid.updateRowChecked(dataGrid.rows[i], true);
                                    }
                                }
                            }
                            dataGrid.updateRowChecked(id, e.target.checked);
                        }}
                        disabled={disabled}
                    />
                </div>
            )}

            {React.Children.toArray(children)
                .filter((c) => c)
                .map((child, idx) => {
                    return (
                        <div
                            key={idx}
                            className="flex items-center pl-4"
                            data-data-grid-col
                            style={{ justifyContent: dataGrid.columns[idx]?.alignment || 'left' }}
                        >
                            {child}
                        </div>
                    );
                })}
        </div>
    );
}

const DataGrid = Object.assign(DataGridForwardRef, { Row });

export default DataGrid;
